import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { formatApiErrorDetail } from '@/utils/authErrors';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoLoginTriggeredRef = useRef(false);

  useEffect(() => {
    if (searchParams.get('demo') !== '1') {
      return;
    }

    try {
      const raw = window.sessionStorage.getItem('demo-login-payload');
      if (!raw) {
        return;
      }
      const payload = JSON.parse(raw);
      setEmail(payload.email || '');
      setPassword(payload.password || '');
      setRememberMe(false);
    } catch (_error) {
      toast.error('Demo hesap bilgileri yüklenemedi');
    }
  }, [searchParams]);

  useEffect(() => {
    const shouldAutoLogin = searchParams.get('autologin') === '1';
    if (!shouldAutoLogin || autoLoginTriggeredRef.current || !email || !password) {
      return;
    }

    autoLoginTriggeredRef.current = true;

    const runAutoLogin = async () => {
      setLoading(true);
      try {
        await login(email, password, false);
        window.sessionStorage.removeItem('demo-login-payload');
        toast.success('Demo hesap ile giriş yapıldı');
        navigate('/dashboard');
      } catch (error) {
        toast.error(formatApiErrorDetail(error.response?.data?.detail || error.message));
      } finally {
        setLoading(false);
      }
    };

    runAutoLogin();
  }, [email, login, navigate, password, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password, rememberMe);
      toast.success('Başarıyla giriş yapıldı');
      navigate('/dashboard');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Giriş Yap"
      subtitle="Hesabına email ve şifrenle güvenli şekilde eriş."
      footer={(
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
          <span>Hesabın yok mu? <Link to="/register" className="font-medium text-sky-700 underline" data-testid="login-register-link">Kayıt ol</Link></span>
          <Link to="/forgot-password" className="font-medium text-sky-700 underline" data-testid="login-forgot-password-link">Şifremi unuttum</Link>
        </div>
      )}
    >
          {searchParams.get('demo') === '1' && (
            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950" data-testid="login-demo-prefill-banner">
              Demo hesap bilgileri forma yüklendi. {searchParams.get('autologin') === '1' ? 'Otomatik giriş deneniyor...' : 'İstersen bilgileri kontrol edip giriş yapabilirsin.'}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm sm:text-base">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="ornek@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
                className="h-10 sm:h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm sm:text-base">Şifre</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
                className="h-10 sm:h-11"
              />
            </div>

            <div className="flex items-center space-x-3 rounded-2xl border border-slate-200 px-4 py-3">
              <Checkbox
                id="remember-me"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(Boolean(checked))}
                data-testid="remember-me-checkbox"
              />
              <Label htmlFor="remember-me" className="text-xs sm:text-sm font-normal cursor-pointer">
                Beni Hatırla (30 gün)
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full h-10 sm:h-11 text-sm sm:text-base"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
            </Button>
          </form>
    </AuthLayout>
  );
}
